import React, { useState, useEffect, useRef } from 'react';
import { leadAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './FollowUpNotification.css';

const FollowUpNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [shownNotificationIds, setShownNotificationIds] = useState(() => {
    // Load previously shown notification IDs from localStorage
    try {
      const stored = localStorage.getItem('completedFollowUpIds');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const audioRef = useRef(null);
  const checkInterval = useRef(null);
  const { currentUser } = useAuth();

  // Persist completed follow-up IDs to localStorage whenever they change
  useEffect(() => {
    try {
      const idsToStore = [...shownNotificationIds];
      // Only keep IDs for last 30 days to prevent localStorage from growing too large
      // (In production, you might want to clean this based on actual completion dates)
      if (idsToStore.length > 1000) {
        // Keep only the most recent 500 IDs
        idsToStore.splice(0, idsToStore.length - 500);
      }
      localStorage.setItem('completedFollowUpIds', JSON.stringify(idsToStore));
      console.log(`[FollowUp] Stored ${idsToStore.length} completed follow-up IDs`);
    } catch (error) {
      console.error('[FollowUp] Error saving to localStorage:', error);
    }
  }, [shownNotificationIds]);

  useEffect(() => {
    // Don't start checking if user is not logged in
    if (!currentUser) {
      console.log('[FollowUp] No user logged in, skipping follow-up checks');
      return;
    }

    console.log('[FollowUp] Starting follow-up notification system for user:', currentUser.name);
    
    // Check for follow-ups every 10 seconds for better responsiveness
    checkFollowUps();
    checkInterval.current = setInterval(checkFollowUps, 10000);

    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        console.log('[FollowUp] Stopped follow-up notification system');
      }
    };
  }, [currentUser]);

  useEffect(() => {
    if (notifications.length > 0 && !showNotification) {
      showNextNotification();
    }
  }, [notifications, showNotification]);

  const checkFollowUps = async () => {
    try {
      console.log('[FollowUp] Checking for due follow-ups...', new Date().toLocaleTimeString());
      
      // Get all leads (will filter based on role)
      const response = await leadAPI.getAll();
      let leads = response.data;

      console.log(`[FollowUp] Fetched ${leads.length} leads from backend`);
      console.log('[FollowUp] Current user:', currentUser);
      console.log('[FollowUp] User role:', currentUser?.role);
      console.log('[FollowUp] User customRole:', currentUser?.customRole);

      // Admin users can see all leads, others only see their assigned leads
      const isAdmin = currentUser?.role === 'Admin' || 
                      currentUser?.role === 'Super Admin' || 
                      currentUser?.customRole === 'Admin' ||
                      currentUser?.email === 'admin@crm.com';
      
      if (!isAdmin) {
        const originalCount = leads.length;
        leads = leads.filter(lead => 
          lead.assignedTo && (lead.assignedTo._id === currentUser.id || lead.assignedTo === currentUser.id)
        );
        console.log(`[FollowUp] User is not admin, filtered from ${originalCount} to ${leads.length} assigned leads`);
      } else {
        console.log(`[FollowUp] User is admin, showing all ${leads.length} leads`);
      }

      const now = new Date();
      const dueFollowUps = [];

      leads.forEach(lead => {
        if (lead.followUps && lead.followUps.length > 0) {
          console.log(`[FollowUp] Lead ${lead.leadId} has ${lead.followUps.length} follow-ups`);
          lead.followUps.forEach(followUp => {
            if (followUp.status === 'Pending' && !shownNotificationIds.has(followUp._id)) {
              const followUpTime = new Date(followUp.scheduledDate);
              const timeDiff = followUpTime - now;
              
              console.log(`[FollowUp] Checking: ${followUp.purpose} scheduled for ${followUpTime.toLocaleString()}, timeDiff: ${Math.floor(timeDiff/1000)}s`);
              
              // Show notification if follow-up is within next 1 minute or has passed
              // Look back up to 2 hours for overdue follow-ups
              if (timeDiff <= 60 * 1000 && timeDiff >= -2 * 60 * 60 * 1000) {
                console.log(`[FollowUp] ✅ DUE! Adding to notifications: ${followUp.purpose}`);
                dueFollowUps.push({
                  ...followUp,
                  lead: {
                    _id: lead._id,
                    leadId: lead.leadId,
                    customerName: lead.customerName,
                    mobile: lead.mobile,
                    email: lead.email,
                  },
                  timeUntil: timeDiff,
                  isOverdue: timeDiff < 0,
                });
              }
            }
          });
        }
      });

      // Sort by time (most overdue first, then by time)
      dueFollowUps.sort((a, b) => a.timeUntil - b.timeUntil);

      // Add new notifications
      if (dueFollowUps.length > 0) {
        console.log(`[FollowUp] Found ${dueFollowUps.length} due follow-ups!`);
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n._id));
          const newNotifs = dueFollowUps.filter(n => !existingIds.has(n._id));
          if (newNotifs.length > 0) {
            console.log(`[FollowUp] Adding ${newNotifs.length} new notifications to queue`);
          }
          return [...prev, ...newNotifs];
        });
      } else {
        console.log('[FollowUp] No due follow-ups found');
      }
    } catch (error) {
      console.error('[FollowUp] Error checking follow-ups:', error);
    }
  };

  const showNextNotification = () => {
    if (notifications.length > 0) {
      const nextNotif = notifications[0];
      setCurrentNotification(nextNotif);
      setShowNotification(true);
      
      // Mark this notification as shown
      setShownNotificationIds(prev => new Set([...prev, nextNotif._id]));
      
      playNotificationSound();
      
      // Request browser notification permission if not granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Follow-up Reminder', {
          body: `${nextNotif.purpose} - ${nextNotif.lead.customerName}`,
          icon: '/favicon.ico',
          tag: nextNotif._id,
        });
      }
    }
  };

  const playNotificationSound = () => {
    // Create notification sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for bell sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Bell frequency
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Second bell tone
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.value = 1000;
        oscillator2.type = 'sine';
        
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.5);
      }, 200);
      
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  };

  const handleDismiss = () => {
    if (currentNotification) {
      // Mark as shown so dismissed notifications don't reappear
      setShownNotificationIds(prev => new Set([...prev, currentNotification._id]));
    }
    setShowNotification(false);
    setNotifications(prev => prev.slice(1));
    setCurrentNotification(null);
  };

  const handleComplete = async () => {
    if (!currentNotification) return;
    
    try {
      console.log('[FollowUp] Marking follow-up as complete:', currentNotification._id);
      console.log('[FollowUp] Lead ID:', currentNotification.lead._id);
      console.log('[FollowUp] Follow-up ID:', currentNotification._id);
      
      // Update follow-up status to Completed
      const response = await leadAPI.updateFollowUpStatus(
        currentNotification.lead._id, 
        currentNotification._id, 
        'Completed'
      );
      
      console.log('[FollowUp] ✅ Follow-up marked as completed:', response);
      
      // Mark as shown permanently so it never appears again
      setShownNotificationIds(prev => new Set([...prev, currentNotification._id]));
      
      // Remove from current notifications queue
      setNotifications(prev => prev.filter(n => n._id !== currentNotification._id));
      
      // Dismiss the notification
      setShowNotification(false);
      setCurrentNotification(null);
      
      // Force a re-check to update the data from backend
      setTimeout(() => {
        checkFollowUps();
      }, 1000);
      
    } catch (error) {
      console.error('[FollowUp] Error marking follow-up as complete:', error);
      console.error('[FollowUp] Error details:', error.response?.data);
      alert('Error: ' + (error.response?.data?.message || error.message || 'Please try again'));
    }
  };

  const handleSnooze = (minutes) => {
    // Snooze for specified minutes
    setShowNotification(false);
    setTimeout(() => {
      if (currentNotification) {
        setNotifications(prev => [...prev, currentNotification]);
      }
    }, minutes * 60 * 1000);
    setNotifications(prev => prev.slice(1));
    setCurrentNotification(null);
  };

  const formatTime = (timeUntil) => {
    const absTime = Math.abs(timeUntil);
    const minutes = Math.floor(absTime / (60 * 1000));
    
    if (timeUntil < 0) {
      if (minutes === 0) return 'Overdue (just now)';
      return `Overdue by ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      if (minutes === 0) return 'Due now';
      return `Due in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  if (!showNotification || !currentNotification) return null;

  return (
    <div className="notification-overlay">
      <div className={`notification-popup ${currentNotification.isOverdue ? 'overdue' : 'upcoming'}`}>
        <div className="notification-header">
          <div className="notification-icon">
            {currentNotification.isOverdue ? '🔴' : '⏰'}
          </div>
          <div className="notification-title">
            <h3>{currentNotification.isOverdue ? 'Overdue Follow-up!' : 'Follow-up Reminder'}</h3>
            <span className="time-badge">{formatTime(currentNotification.timeUntil)}</span>
          </div>
          <button className="close-notif-btn" onClick={handleDismiss}>×</button>
        </div>

        <div className="notification-body">
          <div className="lead-info">
            <div className="info-row">
              <span className="label">Lead ID:</span>
              <span className="value">{currentNotification.lead.leadId}</span>
            </div>
            <div className="info-row">
              <span className="label">Customer:</span>
              <span className="value customer-name">{currentNotification.lead.customerName}</span>
            </div>
            <div className="info-row">
              <span className="label">Mobile:</span>
              <span className="value">
                <a href={`tel:${currentNotification.lead.mobile}`}>
                  📞 {currentNotification.lead.mobile}
                </a>
              </span>
            </div>
            {currentNotification.lead.email && (
              <div className="info-row">
                <span className="label">Email:</span>
                <span className="value">
                  <a href={`mailto:${currentNotification.lead.email}`}>
                    ✉️ {currentNotification.lead.email}
                  </a>
                </span>
              </div>
            )}
          </div>

          <div className="followup-details">
            <h4>📋 Follow-up Details</h4>
            <div className="info-row">
              <span className="label">Scheduled:</span>
              <span className="value">{formatDateTime(currentNotification.scheduledDate)}</span>
            </div>
            <div className="info-row">
              <span className="label">Purpose:</span>
              <span className="value purpose">{currentNotification.purpose}</span>
            </div>
            {currentNotification.notes && (
              <div className="notes-section">
                <span className="label">Notes:</span>
                <p className="notes-text">{currentNotification.notes}</p>
              </div>
            )}
          </div>
        </div>

        <div className="notification-footer">
          <button className="btn btn-success" onClick={handleComplete}>
            ✅ Complete
          </button>
          <button className="btn btn-secondary" onClick={() => handleSnooze(5)}>
            ⏱️ Snooze 5 min
          </button>
          <button className="btn btn-secondary" onClick={() => handleSnooze(15)}>
            ⏱️ Snooze 15 min
          </button>
          <button className="btn btn-primary" onClick={handleDismiss}>
            👍 Got it
          </button>
        </div>

        {notifications.length > 1 && (
          <div className="more-notifications">
            +{notifications.length - 1} more follow-up{notifications.length - 1 !== 1 ? 's' : ''} pending
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUpNotification;
